import * as React from 'react';
import { createLogger } from '@/services/loggingService';

const logger = createLogger('OnboardingContext');

const ONBOARDING_STORAGE_KEY = 'amazon-profit-studio-onboarding';
const WHATS_NEW_VERSION_KEY = 'amazon-profit-studio-whats-new-version';

// Current app version for "What's New" feature
const CURRENT_VERSION = '1.0.0';

export type OnboardingStep =
  | 'welcome'
  | 'file-upload'
  | 'contribution-margin'
  | 'run-analysis'
  | 'results'
  | 'export'
  // Pricing Analysis Tour
  | 'pricing-upload'
  | 'pricing-elasticity'
  | 'pricing-simulate'
  | 'pricing-optimal'
  // Promotions Tour
  | 'promotions-upload'
  | 'promotions-performance'
  | 'promotions-compare'
  | 'promotions-plan'
  // P&L Reporting Tour
  | 'reporting-aggregation'
  | 'reporting-timegroup'
  | 'reporting-yoy'
  | 'reporting-export';

interface OnboardingState {
  hasSeenOnboarding: boolean;
  completedSteps: OnboardingStep[];
  currentTourStep: OnboardingStep | null;
  isTourActive: boolean;
  hasSeenWhatsNew: boolean;
  lastSeenVersion: string | null;
  dismissedTooltips: string[];
}

interface OnboardingContextValue extends OnboardingState {
  startTour: () => void;
  endTour: () => void;
  completeStep: (step: OnboardingStep) => void;
  setCurrentTourStep: (step: OnboardingStep | null) => void;
  dismissOnboarding: () => void;
  resetOnboarding: () => void;
  dismissTooltip: (tooltipId: string) => void;
  isTooltipDismissed: (tooltipId: string) => boolean;
  markWhatsNewSeen: () => void;
  shouldShowWhatsNew: boolean;
}

const defaultState: OnboardingState = {
  hasSeenOnboarding: false,
  completedSteps: [],
  currentTourStep: null,
  isTourActive: false,
  hasSeenWhatsNew: false,
  lastSeenVersion: null,
  dismissedTooltips: [],
};

const OnboardingContext = React.createContext<OnboardingContextValue | undefined>(undefined);

function loadOnboardingState(): OnboardingState {
  try {
    const stored = localStorage.getItem(ONBOARDING_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { ...defaultState, ...parsed };
    }
  } catch (error) {
    logger.warn('Failed to load onboarding state', { error: error instanceof Error ? error.message : String(error) });
  }
  return defaultState;
}

function saveOnboardingState(state: OnboardingState): void {
  try {
    localStorage.setItem(ONBOARDING_STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    logger.warn('Failed to save onboarding state', { error: error instanceof Error ? error.message : String(error) });
  }
}

export const OnboardingProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = React.useState<OnboardingState>(loadOnboardingState);

  // Persist state changes to localStorage
  React.useEffect(() => {
    saveOnboardingState(state);
  }, [state]);

  const startTour = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      isTourActive: true,
      currentTourStep: 'file-upload',
    }));
  }, []);

  const endTour = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      isTourActive: false,
      currentTourStep: null,
      hasSeenOnboarding: true,
    }));
  }, []);

  const completeStep = React.useCallback((step: OnboardingStep) => {
    setState((prev) => {
      if (prev.completedSteps.includes(step)) {
        return prev;
      }
      return {
        ...prev,
        completedSteps: [...prev.completedSteps, step],
      };
    });
  }, []);

  const setCurrentTourStep = React.useCallback((step: OnboardingStep | null) => {
    setState((prev) => ({
      ...prev,
      currentTourStep: step,
    }));
  }, []);

  const dismissOnboarding = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      hasSeenOnboarding: true,
      isTourActive: false,
      currentTourStep: null,
    }));
  }, []);

  const resetOnboarding = React.useCallback(() => {
    setState({
      ...defaultState,
      hasSeenWhatsNew: state.hasSeenWhatsNew,
      lastSeenVersion: state.lastSeenVersion,
    });
  }, [state.hasSeenWhatsNew, state.lastSeenVersion]);

  const dismissTooltip = React.useCallback((tooltipId: string) => {
    setState((prev) => {
      if (prev.dismissedTooltips.includes(tooltipId)) {
        return prev;
      }
      return {
        ...prev,
        dismissedTooltips: [...prev.dismissedTooltips, tooltipId],
      };
    });
  }, []);

  const isTooltipDismissed = React.useCallback(
    (tooltipId: string) => {
      return state.dismissedTooltips.includes(tooltipId);
    },
    [state.dismissedTooltips]
  );

  const markWhatsNewSeen = React.useCallback(() => {
    setState((prev) => ({
      ...prev,
      hasSeenWhatsNew: true,
      lastSeenVersion: CURRENT_VERSION,
    }));
  }, []);

  const shouldShowWhatsNew = React.useMemo(() => {
    // Show "What's New" if user has completed onboarding but hasn't seen this version
    return state.hasSeenOnboarding && state.lastSeenVersion !== CURRENT_VERSION;
  }, [state.hasSeenOnboarding, state.lastSeenVersion]);

  const value = React.useMemo<OnboardingContextValue>(
    () => ({
      ...state,
      startTour,
      endTour,
      completeStep,
      setCurrentTourStep,
      dismissOnboarding,
      resetOnboarding,
      dismissTooltip,
      isTooltipDismissed,
      markWhatsNewSeen,
      shouldShowWhatsNew,
    }),
    [
      state,
      startTour,
      endTour,
      completeStep,
      setCurrentTourStep,
      dismissOnboarding,
      resetOnboarding,
      dismissTooltip,
      isTooltipDismissed,
      markWhatsNewSeen,
      shouldShowWhatsNew,
    ]
  );

  return <OnboardingContext.Provider value={value}>{children}</OnboardingContext.Provider>;
};

export const useOnboarding = () => {
  const context = React.useContext(OnboardingContext);
  if (!context) {
    throw new Error('useOnboarding must be used within an OnboardingProvider');
  }
  return context;
};
